import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useT } from "../i18n/LanguageContext";
import BookCard from "../components/BookCard";

/**
 * Shared pillar page (Body / Mind / Spirit).
 * @param {string} pillar - 'body' | 'mind' | 'spirit'
 */
export default function PillarPage({ pillar }) {
  const t = useT();
  const root = useReveal();
  const data = t(`pillars.${pillar}`);

  // Volume mapping: body→Vol I (image cover), mind→II (SVG), spirit→III (SVG)
  const volMap = {
    body: { num: "Volume I", word: "BODY", image: "/images/cover_body.png" },
    mind: { num: "Volume II", word: "MIND" },
    spirit: { num: "Volume III", word: "SPIRIT" },
  };
  const vol = volMap[pillar];

  return (
    <div ref={root} className="pt-32 md:pt-40">
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="reveal text-[0.7rem] uppercase tracking-[0.42em] text-[#9A7E47]">
              {data.eyebrow} · <span className="text-[#1A1A1A]">{data.word}</span>
            </p>
            <h1 className="reveal reveal-delay-1 mt-8 font-serif text-6xl md:text-8xl lg:text-[9rem] tracking-tight text-[#1A1A1A] leading-[0.85]">
              {data.title}
            </h1>
            <p className="reveal reveal-delay-2 mt-10 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
              {data.intro}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="max-w-sm mx-auto">
              <BookCard
                num={vol.num}
                title={`User Manual for the ${pillar.charAt(0).toUpperCase() + pillar.slice(1)}`}
                sub={data.word}
                word={vol.word}
                image={vol.image}
                testid={`pillar-${pillar}-book`}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F3F1EA] border-y border-[rgba(26,26,26,0.06)] py-24 md:py-32">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="reveal eyebrow">What you learn</p>
              <h2 className="reveal reveal-delay-1 mt-6 font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight leading-[1.05]">
                Inside the Volume.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-10">
              {data.sections.map((s, i) => (
                <div
                  key={i}
                  className={`reveal reveal-delay-${(i % 4) + 1} grid grid-cols-1 md:grid-cols-12 gap-6 pb-8 border-b border-[rgba(26,26,26,0.08)]`}
                >
                  <p className="md:col-span-1 font-serif italic text-[#9A7E47] text-2xl">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div className="md:col-span-11">
                    <h3 className="font-serif text-xl md:text-2xl text-[#1A1A1A] tracking-tight">
                      {s.h}
                    </h3>
                    <p className="mt-3 text-[#4A4A4A] leading-relaxed">{s.b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MANUSCRIPT EXCERPT — opening pages of the Volume */}
      {data.manuscript && (
        <section
          className="container-x py-28 md:py-36"
          data-testid={`pillar-${pillar}-manuscript`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <p className="reveal eyebrow">{data.manuscript.eyebrow}</p>
              <h2 className="reveal reveal-delay-1 mt-6 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
                {data.manuscript.title}
              </h2>
              <div className="hairline-gold mt-8 w-16" />
              <p className="mt-8 text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                <span className="tri inline-block mr-2 align-middle" />
                {vol.num} · Opening pages
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="reveal reveal-delay-2 space-y-6 text-lg text-[#2D2D2D] leading-[1.75] manuscript-prose">
                {data.manuscript.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={i === 0 ? "first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-[#9A7E47]" : ""}
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Cross-pillar links */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(26,26,26,0.06)] border border-[rgba(26,26,26,0.06)]">
          <Link
            to="/curriculum"
            className="bg-[#F8F7F2] hover:bg-[#F3F1EA] transition-colors p-8 md:p-12 group"
            data-testid={`pillar-${pillar}-cta-curriculum`}
          >
            <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
              The Curriculum
            </p>
            <h3 className="mt-5 font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">
              View all three Volumes
            </h3>
            <p className="mt-4 text-[#4A4A4A]">Body · Mind · Spirit, taught as one curriculum.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-[#9A7E47] text-xs uppercase tracking-[0.22em] group-hover:gap-3 transition-all">
              View curriculum <ArrowRight size={14} />
            </span>
          </Link>
          <Link
            to="/method"
            className="bg-[#F8F7F2] hover:bg-[#F3F1EA] transition-colors p-8 md:p-12 group"
            data-testid={`pillar-${pillar}-cta-method`}
          >
            <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
              The Method
            </p>
            <h3 className="mt-5 font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">
              How Sanity is taught
            </h3>
            <p className="mt-4 text-[#4A4A4A]">A five-step arc — from awareness to transformation.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-[#9A7E47] text-xs uppercase tracking-[0.22em] group-hover:gap-3 transition-all">
              Read the method <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
